import { useState, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ContactForm } from './ContactForm'
import { useContact, useCreateContact, useUpdateContact } from '@/hooks/useContacts'
import type { ContactFormValues } from './contact-form-schema'
import { getContactDisplayName } from '@/types/contacts'

interface ContactSheetProps {
  /** Whether the dialog is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** Client ID the contact belongs to */
  clientId: string
  /** Account ID for RLS */
  organizationId: string
  /** Contact ID for edit mode, undefined for create mode */
  contactId?: string
}

/**
 * Dialog wrapper for contact create/edit form
 */
export function ContactSheet({
  open,
  onOpenChange,
  clientId,
  organizationId,
  contactId,
}: ContactSheetProps) {
  const isEdit = !!contactId

  // Form dirty state
  const [isDirty, setIsDirty] = useState(false)
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [pendingClose, setPendingClose] = useState(false)

  // Reset dirty state when sheet opens
  // This is intentional - we want to reset state when parent opens the sheet
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsDirty(false)
    }
  }, [open])

  // Fetch existing contact for edit mode
  const { data: contact, isLoading: isLoadingContact } = useContact(
    isEdit ? contactId : undefined
  )

  // Mutations
  const createContact = useCreateContact()
  const updateContact = useUpdateContact()

  const isLoading =
    isLoadingContact || createContact.isPending || updateContact.isPending

  // Handle sheet open change with dirty state check
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        setPendingClose(true)
        setShowExitConfirm(true)
        return
      }
      onOpenChange(newOpen)
      if (!newOpen) {
        setIsDirty(false)
      }
    },
    [isDirty, onOpenChange]
  )

  // Handle confirmed close (discard changes)
  const handleConfirmClose = useCallback(() => {
    setShowExitConfirm(false)
    setIsDirty(false)
    if (pendingClose) {
      setPendingClose(false)
      onOpenChange(false)
    }
  }, [pendingClose, onOpenChange])

  // Handle cancel close (continue editing)
  const handleCancelClose = useCallback(() => {
    setShowExitConfirm(false)
    setPendingClose(false)
  }, [])

  // Handle form submission
  const handleSubmit = async (data: ContactFormValues) => {
    try {
      if (isEdit && contactId) {
        await updateContact.mutateAsync({
          id: contactId,
          clientId,
          data: {
            first_name: data.first_name,
            last_name: data.last_name,
            job_title: data.job_title ?? undefined,
            email_primary: data.email_primary,
            email_secondary: data.email_secondary ?? undefined,
            phone_office: data.phone_office ?? undefined,
            phone_mobile: data.phone_mobile ?? undefined,
            phone_other: data.phone_other ?? undefined,
            is_primary_contact: data.is_primary_contact,
            is_billing_contact: data.is_billing_contact,
            is_operations_contact: data.is_operations_contact,
            preferred_contact_method: data.preferred_contact_method,
            status: data.status,
            notes: data.notes ?? undefined,
          },
        })
      } else {
        await createContact.mutateAsync({
          client_id: clientId,
          organization_id: organizationId,
          first_name: data.first_name,
          last_name: data.last_name,
          job_title: data.job_title || undefined,
          email_primary: data.email_primary,
          email_secondary: data.email_secondary || undefined,
          phone_office: data.phone_office || undefined,
          phone_mobile: data.phone_mobile || undefined,
          phone_other: data.phone_other || undefined,
          is_primary_contact: data.is_primary_contact,
          is_billing_contact: data.is_billing_contact,
          is_operations_contact: data.is_operations_contact,
          preferred_contact_method: data.preferred_contact_method,
          status: data.status,
          notes: data.notes || undefined,
        })
      }

      // Success - close sheet
      setIsDirty(false)
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving contact:', error)
    }
  }

  // Handle cancel button
  const handleCancel = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  // Prevent closing on interact outside when dirty
  const handleInteractOutside = useCallback(
    (e: Event) => {
      if (isDirty) {
        e.preventDefault()
        setShowExitConfirm(true)
      }
    },
    [isDirty]
  )

  // Prevent closing on escape when dirty
  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isDirty) {
        e.preventDefault()
        setShowExitConfirm(true)
      }
    },
    [isDirty]
  )

  // Track form dirty state via form onChange
  const handleFormChange = useCallback(() => {
    if (!isDirty) {
      setIsDirty(true)
    }
  }, [isDirty])

  const dialogTitle = isEdit
    ? `Edit Contact: ${contact ? getContactDisplayName(contact) : '...'}`
    : 'New Contact'

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] !flex !flex-col overflow-hidden"
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1" onChange={handleFormChange}>
            {isEdit && isLoadingContact ? (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                Loading contact...
              </div>
            ) : (
              <ContactForm
                key={contactId ?? 'create'}
                contact={contact}
                onSubmit={handleSubmit}
                isLoading={isLoading}
              />
            )}
          </div>

          <DialogFooter className="pt-4 border-t flex-shrink-0">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" form="contact-form" disabled={isLoading}>
              {isLoading ? 'Saving...' : isEdit ? 'Update Contact' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close? Your
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
